import { cn } from '@poseidon/components/cn';

import type { TouchStepContactStatus } from './touchGraphLayout';

/**
 * Visual bucket for a step's per-contact status. The buckets must be told apart at a
 * glance: `action` (you still have to do something) is ORANGE and carries a button;
 * `done` (finished / replied) is solid GREEN; `skipped` is green struck through with a
 * 45° diagonal hatch (the sequence auto-advanced past it — on the happy path, but this
 * touch never actually ran); `error` is RED and also flags an INTERRUPTED step — the
 * campaign stalled there before this step could run (mirrors the red treatment #2395
 * gives interrupted steps in the list view); the remaining skip-ish states (canceled /
 * unavailable) are muted gray. `not-started` (and anything unmapped) returns null → the
 * row renders neutral, exactly as the template page does. An error message always wins
 * (a step can carry one in a non-terminal state).
 */
export type StatusTone = 'done' | 'action' | 'error' | 'muted' | 'skipped';

/**
 * `tone` drives the color, `label` the badge text, and `tooltip` the hover
 * explanation of WHY the step is that color (the runtime error, the skip reason,
 * "the campaign stalled here", etc.) — shown on the row and on the minimal icon
 * chip, which otherwise has no words to explain its tint.
 */
export function stepStatusTone(
  status: TouchStepContactStatus | null,
): { tone: StatusTone; label: string; tooltip: string } | null {
  if (status == null) return null;
  if (status.errorMessage != null) return { tone: 'error', label: 'Errored', tooltip: status.errorMessage };
  switch (status.state) {
    case 'action-required':
      return {
        tone: 'action',
        label: 'Action needed',
        tooltip: 'This step needs you to complete a manual action before the campaign continues.',
      };
    case 'finished':
      return { tone: 'done', label: 'Done', tooltip: 'This step ran and completed.' };
    case 'responded':
      return { tone: 'done', label: 'Replied', tooltip: 'The contact replied — this step is complete.' };
    case 'skipped':
      return {
        tone: 'skipped',
        label: 'Skipped',
        tooltip:
          status.skipReason != null
            ? `Skipped: ${status.skipReason}`
            : 'The sequence advanced past this step without running it.',
      };
    case 'canceled':
      return { tone: 'muted', label: 'Canceled', tooltip: 'This step was canceled and will not run.' };
    case 'contact_unavailable':
      return { tone: 'muted', label: 'Unavailable', tooltip: 'This step can’t run — the contact is unavailable.' };
    case 'interrupted':
      // The campaign stalled here — red, not muted, so a broken step doesn't hide as
      // a faded skip-ish one (matches #2395's interrupted treatment in the list view).
      return {
        tone: 'error',
        label: 'Interrupted',
        tooltip: 'The campaign was interrupted before this step could run.',
      };
    default:
      return null; // not-started → neutral
  }
}

/**
 * 45° diagonal hatch — 1.5px mantis lines every 5px — layered as a background *image*
 * over a green fill (the fill is a separate `bg-*` color, which shows through the
 * transparent gaps). Marks a SKIPPED step: green because the sequence advanced past it
 * cleanly (unlike a red error or a gray cancel), struck through with diagonal lines
 * because this touch never actually ran for the contact. A lighter line in dark mode so
 * it reads on the darker green base. Same technique as `CounterBox`'s disabled hatch.
 * Tune geometry here: `_3.5px` = gap, `_5px` = period (line width = 5 − 3.5 = 1.5px).
 */
export const SKIPPED_HATCH =
  'bg-[repeating-linear-gradient(45deg,transparent,transparent_3.5px,var(--color-mantis-500)_3.5px,var(--color-mantis-500)_5px)] ' +
  'dark:bg-[repeating-linear-gradient(45deg,transparent,transparent_3.5px,var(--color-mantis-400)_3.5px,var(--color-mantis-400)_5px)]';

/**
 * Row background tint for a status tone. `done` is empty (it reads via its green
 * badge); `action` gets an orange wash so a step you still owe an action on stands
 * apart from a finished (green) one. `skipped` gets a soft green fill under the hatch.
 */
export const TONE_ROW_CLASS: Record<StatusTone, string> = {
  done: '',
  action: 'bg-blaze-orange-50 dark:bg-blaze-orange-500/10',
  error: 'bg-fair-pink-50 ring-1 ring-inset ring-fair-pink-300 dark:bg-fair-pink-950/40 dark:ring-fair-pink-500/40',
  muted: 'opacity-60',
  skipped: cn('bg-mantis-50 dark:bg-mantis-950', SKIPPED_HATCH),
};

/** Small status pill shown next to the step-type label — orange = act, red = error, green = done. */
export const TONE_BADGE_CLASS: Record<StatusTone, string> = {
  done: 'bg-mantis-100 text-mantis-700 dark:bg-mantis-900 dark:text-mantis-200',
  action: 'bg-blaze-orange-100 text-blaze-orange-700 dark:bg-blaze-orange-900 dark:text-blaze-orange-200',
  error: 'bg-fair-pink-100 text-fair-pink-700 dark:bg-fair-pink-950 dark:text-fair-pink-300',
  muted: 'bg-lynch-100 text-lynch-600 dark:bg-slate-800 dark:text-lynch-300',
  // Solid green pill; the "Skipped" label (not the hatch) tells it apart from "Done"
  // so the small pill text stays legible.
  skipped: 'bg-mantis-100 text-mantis-700 dark:bg-mantis-900 dark:text-mantis-200',
};

/** Minimal-mode icon-chip tint by status tone (falls back to the neutral chip). */
export const TONE_CHIP_CLASS: Record<StatusTone, string> = {
  done: 'bg-mantis-100 text-mantis-700 dark:bg-mantis-900 dark:text-mantis-200',
  action:
    'bg-blaze-orange-100 text-blaze-orange-700 ring-1 ring-blaze-orange-300 dark:bg-blaze-orange-900 dark:text-blaze-orange-200',
  error: 'bg-fair-pink-100 text-fair-pink-600 ring-1 ring-fair-pink-300 dark:bg-fair-pink-950 dark:text-fair-pink-300',
  muted: 'bg-lynch-50 text-lynch-400 opacity-60 dark:bg-slate-800',
  // Same green as `done`, struck through with the diagonal hatch; the step icon renders
  // on top (foreground), the hatch/fill sit behind it as backgrounds.
  skipped: cn('bg-mantis-100 text-mantis-700 dark:bg-mantis-900 dark:text-mantis-200', SKIPPED_HATCH),
};
